import { useMemo, useState } from "react";
import { useStore, uid } from "../store/store";
import { useT } from "../i18n/useT";
import { REMOVAL_REASONS } from "@mensajeando/compliance";
import type { Contact, Language } from "../store/types";

export function Contacts() {
  const t = useT();
  const contacts = useStore((s) => s.contacts);
  const addContact = useStore((s) => s.addContact);
  const updateContact = useStore((s) => s.updateContact);
  const addConsent = useStore((s) => s.addConsent);
  const orgId = useStore((s) => s.organizations[0]?.id) ?? "org_main";
  const clientId = useStore((s) => s.clients[0]?.id) ?? "client_demo";

  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState<Partial<Contact>>({ language: "en" });
  const [csvOpen, setCsvOpen] = useState(false);
  const [csv, setCsv] = useState("");

  const stats = useMemo(() => ({
    total: contacts.length,
    suppressed: contacts.filter((c) => c.suppressed).length,
    es: contacts.filter((c) => c.language === "es").length,
    en: contacts.filter((c) => c.language === "en").length,
  }), [contacts]);

  function save() {
    if (!draft.phoneE164 || !draft.firstName) return;
    const c: Contact = {
      id: uid("c"),
      organizationId: orgId,
      clientId,
      firstName: draft.firstName,
      lastName: draft.lastName,
      phoneE164: draft.phoneE164,
      language: (draft.language as Language) ?? "en",
      timezone: draft.timezone ?? "America/New_York",
      tags: [],
      suppressed: false,
    };
    addContact(c);
    addConsent({
      id: uid("consent"),
      contactId: c.id,
      channel: "sms",
      language: c.language,
      status: "granted",
      source: "verbal_logged",
      disclosureVersion: "2026-01-15.v1",
      at: new Date().toISOString(),
    });
    setShowAdd(false);
    setDraft({ language: "en" });
  }

  function importCsv() {
    // Expected header: firstName,phoneE164,language,timezone
    const lines = csv.trim().split(/\r?\n/);
    const [header, ...rows] = lines;
    if (!header) return;
    const cols = header.split(",").map((c) => c.trim());
    const idx = (k: string) => cols.indexOf(k);
    let imported = 0, dupes = 0;
    const seen = new Set(contacts.map((c) => c.phoneE164));
    for (const r of rows) {
      const fields = r.split(",").map((s) => s.trim());
      const phone = fields[idx("phoneE164")];
      if (!phone) continue;
      if (seen.has(phone)) { dupes += 1; continue; }
      seen.add(phone);
      addContact({
        id: uid("c"),
        organizationId: orgId,
        clientId,
        firstName: fields[idx("firstName")] ?? "",
        phoneE164: phone,
        language: (fields[idx("language")] as Language) ?? "en",
        timezone: fields[idx("timezone")] ?? "America/New_York",
        tags: [],
        suppressed: false,
      });
      imported += 1;
    }
    alert(`Imported ${imported}, deduped ${dupes}`);
    setCsv("");
    setCsvOpen(false);
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.nav.contacts}</h1>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setCsvOpen(true)}>{t.contacts.import}</button>
          <button className="btn-primary" onClick={() => setShowAdd(true)}>+ {t.contacts.addManually}</button>
        </div>
      </header>

      <section className="grid grid-cols-4 gap-4 mb-6">
        <Stat label={t.contacts.count} value={stats.total} />
        <Stat label={t.contacts.suppressed} value={stats.suppressed} />
        <Stat label="EN preference" value={stats.en} />
        <Stat label="ES preference" value={stats.es} />
      </section>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left p-3">{t.contacts.firstName}</th>
              <th className="text-left p-3">{t.contacts.phone}</th>
              <th className="text-left p-3">{t.common.language}</th>
              <th className="text-left p-3">Timezone</th>
              <th className="text-left p-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="p-3">{c.firstName} {c.lastName ?? ""}</td>
                <td className="p-3 font-mono text-xs">{c.phoneE164}</td>
                <td className="p-3 uppercase">{c.language}</td>
                <td className="p-3 text-slate-600">{c.timezone ?? "—"}</td>
                <td className="p-3">
                  {c.suppressed ? <span className="badge-err">Suppressed</span> : <span className="badge-ok">Active</span>}
                </td>
                <td className="p-3 text-right">
                  {!c.suppressed ? (
                    <select
                      className="text-xs border border-slate-300 rounded px-1 py-0.5"
                      defaultValue=""
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const reason = REMOVAL_REASONS.find((r) => r.code === e.target.value);
                        updateContact(c.id, { suppressed: true, suppressionReason: reason?.label_en });
                      }}
                    >
                      <option value="">Suppress…</option>
                      {REMOVAL_REASONS.map((r) => (
                        <option key={r.code} value={r.code}>{r.label_en}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      className="text-xs text-brand-700 hover:underline"
                      onClick={() => updateContact(c.id, { suppressed: false, suppressionReason: undefined })}
                    >Unsuppress</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <Modal title={t.contacts.addManually} onClose={() => setShowAdd(false)}>
          <div className="space-y-3">
            <div>
              <label className="label">{t.contacts.firstName}</label>
              <input className="input" value={draft.firstName ?? ""} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
            </div>
            <div>
              <label className="label">{t.contacts.phone}</label>
              <input className="input" placeholder="+14045550100" value={draft.phoneE164 ?? ""} onChange={(e) => setDraft({ ...draft, phoneE164: e.target.value })} />
            </div>
            <div>
              <label className="label">{t.common.language}</label>
              <select className="input" value={draft.language} onChange={(e) => setDraft({ ...draft, language: e.target.value as Language })}>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">A consent record will be created with source <code>verbal_logged</code>. Replace with actual opt-in proof before going live.</p>
            <div className="flex gap-2 justify-end">
              <button className="btn-secondary" onClick={() => setShowAdd(false)}>{t.common.cancel}</button>
              <button className="btn-primary" onClick={save}>{t.common.save}</button>
            </div>
          </div>
        </Modal>
      )}

      {csvOpen && (
        <Modal title={t.contacts.import} onClose={() => setCsvOpen(false)}>
          <p className="text-xs text-slate-500 mb-2">Header: <code>firstName,phoneE164,language,timezone</code>. Duplicates by phone are skipped.</p>
          <textarea className="input h-48 font-mono text-xs" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="firstName,phoneE164,language,timezone&#10;Maria,+14045550111,es,America/New_York" />
          <div className="flex gap-2 justify-end mt-3">
            <button className="btn-secondary" onClick={() => setCsvOpen(false)}>{t.common.cancel}</button>
            <button className="btn-primary" onClick={importCsv}>Import</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-slate-900/40 grid place-items-center z-50" onClick={onClose}>
      <div className="card card-pad w-[520px] max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}
